import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDashboard.css';
import AdminLogin from './AdminLogin';

const API_URL = process.env.REACT_APP_API_URL || '';

// Configure axios defaults
const getAxiosConfig = () => ({
  headers: {
    'X-Auth-Token': localStorage.getItem('authToken') || ''
  }
});

const AdminDashboard = ({ onLogin: onLoginCallback }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingStep, setEditingStep] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [message, setMessage] = useState('');

  // Track active tab for each step with conditional options
  const [activeConditionKeys, setActiveConditionKeys] = useState({});

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setAuthChecking(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/check`, {
        headers: { 'X-Auth-Token': token }
      });
      if (response.data.authenticated) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      localStorage.removeItem('authToken');
    } finally {
      setAuthChecking(false);
    }
  };

  const handleLogin = (token) => {
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);
    if (onLoginCallback) onLoginCallback();
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, getAxiosConfig());
    } catch (error) {
      // Ignore errors on logout
    }
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadConfig();
    }
  }, [isAuthenticated]);

  // Set default active tabs when config loads
  useEffect(() => {
    if (config?.steps) {
      const newActiveKeys = { ...activeConditionKeys };
      let changed = false;

      config.steps.forEach(step => {
        if (step.conditionalOptions && !newActiveKeys[step.key]) {
          // Default to first key
          const keys = Object.keys(step.conditionalOptions);
          if (keys.length > 0) {
            newActiveKeys[step.key] = keys[0];
            changed = true;
          }
        }
      });

      if (changed) {
        setActiveConditionKeys(newActiveKeys);
      }
    }
  }, [config]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/config`, getAxiosConfig());
      setConfig(response.data.config);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        return;
      }
      showMessage('Error loading configuration: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(''), 3000);
  };

  const handleTabChange = (stepKey, conditionKey) => {
    setActiveConditionKeys(prev => ({
      ...prev,
      [stepKey]: conditionKey
    }));
  };

  const updateBasePrice = async (newPrice) => {
    try {
      await axios.put(`${API_URL}/api/admin/config/base-price`, {
        basePrice: parseFloat(newPrice)
      }, getAxiosConfig());
      await loadConfig();
      showMessage('Base price updated successfully');
    } catch (error) {
      showMessage('Error updating base price: ' + error.message, 'error');
    }
  };

  const deleteStep = async (stepKey) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/steps/${stepKey}`, getAxiosConfig());
      await loadConfig();
      showMessage('Step deleted successfully');
    } catch (error) {
      showMessage('Error deleting step: ' + error.message, 'error');
    }
  };

  const deleteOption = async (stepKey, optionValue, conditionKey = null) => {
    if (!window.confirm('Are you sure you want to delete this option?')) return;

    try {
      if (conditionKey) {
        // specialized deletion logic for conditional options
        // We need to fetch the step, modify the conditional array locally, and put the whole step back
        const step = config.steps.find(s => s.key === stepKey);
        if (!step || !step.conditionalOptions[conditionKey]) return;

        const updatedOptions = step.conditionalOptions[conditionKey].filter(o => o.value !== optionValue);

        const updatedStep = {
          ...step,
          conditionalOptions: {
            ...step.conditionalOptions,
            [conditionKey]: updatedOptions
          }
        };

        await axios.put(`${API_URL}/api/admin/steps/${stepKey}`, updatedStep, getAxiosConfig());

      } else {
        // standard option deletion
        await axios.delete(`${API_URL}/api/admin/steps/${stepKey}/options/${optionValue}`, getAxiosConfig());
      }

      await loadConfig();
      showMessage('Option deleted successfully');
    } catch (error) {
      showMessage('Error deleting option: ' + error.message, 'error');
    }
  };

  const updateOption = async (stepKey, optionValue, updatedFields, conditionKey = null) => {
    try {
      if (conditionKey) {
        // Manual update for condition logic
        const step = config.steps.find(s => s.key === stepKey);
        if (!step || !step.conditionalOptions[conditionKey]) return;

        const updatedOptions = step.conditionalOptions[conditionKey].map(o => {
          if (o.value === optionValue) {
            // Don't allowing changing value key here easily without complex logic, so assuming value is constant or user is careful
            return { ...o, ...updatedFields, value: o.value };
          }
          return o;
        });

        const updatedStep = {
          ...step,
          conditionalOptions: {
            ...step.conditionalOptions,
            [conditionKey]: updatedOptions
          }
        };

        await axios.put(`${API_URL}/api/admin/steps/${stepKey}`, updatedStep, getAxiosConfig());

      } else {
        await axios.put(`${API_URL}/api/admin/steps/${stepKey}/options/${optionValue}`, updatedFields, getAxiosConfig());
      }

      await loadConfig();
      setEditingOption(null);
      showMessage('Option updated successfully');
    } catch (error) {
      showMessage('Error updating option: ' + error.message, 'error');
    }
  };

  const addOption = async (stepKey, newOption, conditionKey = null) => {
    try {
      if (conditionKey) {
        const step = config.steps.find(s => s.key === stepKey);
        if (!step || !step.conditionalOptions[conditionKey]) return;

        // Check duplicate
        if (step.conditionalOptions[conditionKey].find(o => o.value === newOption.value)) {
          alert('Option value already exists in this condition set');
          return;
        }

        const updatedOptions = [...step.conditionalOptions[conditionKey], newOption];

        const updatedStep = {
          ...step,
          conditionalOptions: {
            ...step.conditionalOptions,
            [conditionKey]: updatedOptions
          }
        };

        await axios.put(`${API_URL}/api/admin/steps/${stepKey}`, updatedStep, getAxiosConfig());

      } else {
        await axios.post(`${API_URL}/api/admin/steps/${stepKey}/options`, newOption, getAxiosConfig());
      }

      await loadConfig();
      showMessage('Option added successfully');
    } catch (error) {
      showMessage('Error adding option: ' + error.message, 'error');
    }
  };

  const addStep = async (newStep) => {
    try {
      await axios.post(`${API_URL}/api/admin/steps`, newStep, getAxiosConfig());
      await loadConfig();
      showMessage('Step added successfully');
    } catch (error) {
      showMessage('Error adding step: ' + error.message, 'error');
    }
  };

  // Update step metadata (name, description, required, stepNumber)
  const updateStep = async (stepKey, updatedFields) => {
    try {
      await axios.put(`${API_URL}/api/admin/steps/${stepKey}`, updatedFields, getAxiosConfig());
      await loadConfig();
      setEditingStep(null);
      showMessage('Step updated successfully');
    } catch (error) {
      showMessage('Error updating step: ' + error.message, 'error');
    }
  };

  // Duplicate an option with a new value
  const duplicateOption = async (stepKey, option, conditionKey = null) => {
    const newValue = prompt('Enter new value for duplicated option:', option.value + '-copy');
    if (!newValue || newValue === option.value) return;

    const duplicatedOption = {
      ...option,
      value: newValue,
      label: option.label + ' (Copy)'
    };

    try {
      await addOption(stepKey, duplicatedOption, conditionKey);
      showMessage('Option duplicated successfully');
    } catch (error) {
      showMessage('Error duplicating option: ' + error.message, 'error');
    }
  };

  // Show loading while checking auth
  if (authChecking) {
    return <div className="admin-loading">Checking authentication...</div>;
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return <div className="admin-loading">Loading admin panel...</div>;
  }

  if (!config) {
    return <div className="admin-error">Failed to load configuration</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-content">
          <h1>🛠️ Product Configurator Admin</h1>
          <p>Manage your workbench configuration steps and pricing</p>
        </div>
        <button className="btn btn-logout" onClick={handleLogout}>
          Logout
        </button>
      </header>

      {message && (
        <div className={`admin-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Base Price Section */}
      <section className="admin-section">
        <h2>Base Price</h2>
        <div className="base-price-control">
          <label>
            Starting Price: $
            <input
              type="number"
              step="0.01"
              value={config.basePrice}
              onChange={(e) => updateBasePrice(e.target.value)}
              className="price-input"
            />
          </label>
        </div>
      </section>

      {/* Steps Management */}
      <section className="admin-section">
        <div className="section-header">
          <h2>Configuration Steps ({config.steps.length})</h2>
          <button
            className="btn btn-primary"
            onClick={() => {
              const key = prompt('Enter step key (e.g., "size"):');
              if (!key) return;
              const name = prompt('Enter step name (e.g., "Select Size"):');
              if (!name) return;
              const stepNumber = parseInt(prompt('Enter step number:', config.steps.length + 1));

              addStep({
                key,
                name,
                stepNumber,
                required: true,
                description: '',
                options: []
              });
            }}
          >
            + Add New Step
          </button>
        </div>

        {config.steps.map((step, index) => {
          // Determine existing active key for this step if conditional
          const conditionalKeys = step.conditionalOptions ? Object.keys(step.conditionalOptions) : [];
          const activeConditionKey = activeConditionKeys[step.key] || (conditionalKeys.length > 0 ? conditionalKeys[0] : null);

          // Determine which set of options to render
          const optionsToRender = step.conditionalOptions
            ? (step.conditionalOptions[activeConditionKey] || [])
            : (step.options || []);

          return (
            <div key={step.key} className="step-card">
              <div className="step-header">
                {editingStep === step.key ? (
                  // Edit mode for step metadata
                  <div className="step-edit-form" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        Step #:
                        <input
                          type="number"
                          defaultValue={step.stepNumber}
                          id={`step-number-${step.key}`}
                          style={{ width: '60px' }}
                        />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                        Name:
                        <input
                          type="text"
                          defaultValue={step.name}
                          id={`step-name-${step.key}`}
                          style={{ flex: 1 }}
                        />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="checkbox"
                          defaultChecked={step.required}
                          id={`step-required-${step.key}`}
                        />
                        Required
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                        Description:
                        <input
                          type="text"
                          defaultValue={step.description}
                          id={`step-desc-${step.key}`}
                          style={{ flex: 1 }}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn-small btn-primary"
                        onClick={() => {
                          const newName = document.getElementById(`step-name-${step.key}`).value;
                          const newDesc = document.getElementById(`step-desc-${step.key}`).value;
                          const newNumber = parseInt(document.getElementById(`step-number-${step.key}`).value);
                          const newRequired = document.getElementById(`step-required-${step.key}`).checked;
                          updateStep(step.key, {
                            name: newName,
                            description: newDesc,
                            stepNumber: newNumber,
                            required: newRequired
                          });
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => setEditingStep(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <>
                    <div className="step-title">
                      <span className="step-number">{step.stepNumber}</span>
                      <h3>{step.name}</h3>
                      <span className="step-key">({step.key})</span>
                      {step.required && <span className="step-required-badge" style={{ marginLeft: '8px', fontSize: '0.75em', backgroundColor: '#dc3545', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>Required</span>}
                    </div>
                    <div className="step-actions">
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => setEditingStep(step.key)}
                      >
                        Edit Step
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => deleteStep(step.key)}
                      >
                        Delete Step
                      </button>
                    </div>
                  </>
                )}
              </div>

              {!editingStep && <p className="step-description">{step.description || 'No description'}</p>}

              <div className="options-section">
                <div className="options-header">
                  <h4>
                    Options ({optionsToRender.length})
                    {step.conditionalOptions && ' (Conditional)'}
                  </h4>

                  {/* Only allow adding options if we have a valid context (standard or selected condition) */}
                  {(step.options || activeConditionKey) && (
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => {
                        const value = prompt('Enter option value (e.g., "small"):');
                        if (!value) return;
                        const label = prompt('Enter option label (e.g., "Small Size"):');
                        if (!label) return;
                        const priceModifier = parseFloat(prompt('Enter price modifier (e.g., 100):') || '0');

                        addOption(step.key, {
                          value,
                          label,
                          description: '',
                          priceModifier
                        }, activeConditionKey); // Pass condition key if active
                      }}
                    >
                      + Add Option
                    </button>
                  )}
                </div>

                {step.conditionalOptions ? (
                  <div className="conditional-tabs-container">
                    <div className="conditional-tabs">
                      {conditionalKeys.map(key => (
                        <button
                          key={key}
                          className={`tab-btn ${activeConditionKey === key ? 'active' : ''}`}
                          onClick={() => handleTabChange(step.key, key)}
                        >
                          When "{key}" selected
                        </button>
                      ))}
                    </div>
                    <div className="tab-hint">
                      Showing options available when <strong>{activeConditionKey}</strong> is selected previously.
                    </div>
                  </div>
                ) : null}

                <div className="options-list">
                  {optionsToRender.map((option) => (
                    <div key={option.value} className="option-card">
                      {editingOption?.stepKey === step.key && editingOption?.value === option.value ? (
                        <OptionEditor
                          option={option}
                          onSave={(updated) => updateOption(step.key, option.value, updated, activeConditionKey)}
                          onCancel={() => setEditingOption(null)}
                        />
                      ) : (
                        <>
                          <div className="option-info">
                            {option.image && (
                              <div className="option-image-thumb">
                                <img
                                  src={`${API_URL}/uploads/images/${option.image}`}
                                  alt={option.label}
                                  style={{ width: '100%', height: 'auto', borderRadius: '4px', marginBottom: '8px' }}
                                />
                              </div>
                            )}
                            <div className="option-name">{option.label}</div>
                            <div className="option-value">Value: {option.value}</div>
                            {option.description && (
                              <div className="option-desc">{option.description}</div>
                            )}
                            {option.sku && (
                              <div className="option-sku" style={{ fontSize: '0.85em', color: '#666' }}>SKU: {option.sku}</div>
                            )}
                            <div className="option-price">
                              {option.price ? `Base: $${option.price.toFixed(2)}` : `Price: ${option.priceModifier > 0 ? '+' : ''}$${option.priceModifier?.toFixed(2) || '0.00'}`}
                            </div>
                          </div>
                          <div className="option-actions">
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => setEditingOption({ stepKey: step.key, value: option.value })}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-small"
                              onClick={() => duplicateOption(step.key, option, activeConditionKey)}
                              style={{ backgroundColor: '#17a2b8', color: 'white' }}
                            >
                              Duplicate
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => deleteOption(step.key, option.value, activeConditionKey)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {optionsToRender.length === 0 && (
                    <div className="no-options">No options defined for this condition.</div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </section>
    </div>
  );
};

const OptionEditor = ({ option, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    label: option.label,
    description: option.description || '',
    priceModifier: option.priceModifier || 0,
    sku: option.sku || '',
    shopifyVariantId: option.shopifyVariantId || '',
    price: option.price || '',
    image: option.image || ''
  });
  const [uploading, setUploading] = useState(false);
  const [svgComponentType, setSvgComponentType] = useState('');
  const [svgStatus, setSvgStatus] = useState(null);

  // SVG component types that can be generated from uploaded images
  const svgComponentTypes = [
    { value: '', label: 'No SVG generation' },
    { value: '2-leg-frame', label: '2-Leg Frame' },
    { value: '4-leg-frame', label: '4-Leg Frame' },
    { value: 'surface-butcher', label: 'Butcher Block Surface' },
    { value: 'surface-laminate', label: 'Laminate Surface' },
    { value: 'surface-esd', label: 'ESD Surface' },
    { value: 'accessory-drawer', label: 'Drawer Accessory' },
    { value: 'accessory-power', label: 'Power Strip' }
  ];

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    try {
      setUploading(true);
      setSvgStatus(null);
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      // Include component type for SVG generation
      if (svgComponentType) {
        formDataUpload.append('componentType', svgComponentType);
      }

      const response = await axios.post(`${API_URL}/api/images/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Auth-Token': localStorage.getItem('authToken') || ''
        }
      });

      if (response.data.success) {
        setFormData({ ...formData, image: response.data.filename });

        // Report SVG generation status or general success
        if (response.data.svgGenerated) {
          setSvgStatus({ success: true, message: `SVG generated for ${svgComponentType}!` });
        } else if (response.data.svgError) {
          setSvgStatus({ success: false, message: `SVG generation failed: ${response.data.svgError}` });
        } else {
          // Standard upload success message
          setSvgStatus({ success: true, message: 'Image uploaded successfully!' });
        }
      }
    } catch (error) {
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input so same file can be selected again if needed
      e.target.value = null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="option-editor">
      <div className="form-group">
        <label>Label:</label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          required
        />
      </div>
      <div className="form-group">
        <label>Description:</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows="2"
        />
      </div>
      <div className="form-group">
        <label>Price Modifier ($):</label>
        <input
          type="number"
          step="0.01"
          value={formData.priceModifier}
          onChange={(e) => setFormData({ ...formData, priceModifier: parseFloat(e.target.value) || 0 })}
        />
      </div>
      <div className="form-group">
        <label>Base Price ($):</label>
        <input
          type="number"
          step="0.01"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || '' })}
          placeholder="For table size options"
        />
      </div>
      <div className="form-group">
        <label>SKU:</label>
        <input
          type="text"
          value={formData.sku}
          onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
          placeholder="e.g., NXCE502248"
        />
      </div>
      <div className="form-group">
        <label>Shopify Variant ID:</label>
        <input
          type="text"
          value={formData.shopifyVariantId}
          onChange={(e) => setFormData({ ...formData, shopifyVariantId: e.target.value })}
          placeholder="e.g., 40123456789012"
        />
      </div>
      <div className="form-group">
        <label>Product Image:</label>
        {formData.image && (
          <div className="image-preview">
            <img
              src={`${API_URL}/uploads/images/${formData.image}`}
              alt="Product preview"
              style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }}
            />
            <button
              type="button"
              className="btn btn-small btn-danger"
              onClick={() => setFormData({ ...formData, image: '' })}
            >
              Remove Image
            </button>
          </div>
        )}
        <div className="svg-generation-section" style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <label style={{ fontSize: '0.9em', fontWeight: 'bold', color: '#495057' }}>
            🎨 Auto-Generate SVG Preview:
          </label>
          <select
            value={svgComponentType}
            onChange={(e) => setSvgComponentType(e.target.value)}
            style={{ width: '100%', padding: '6px', marginTop: '5px', borderRadius: '4px', border: '1px solid #ced4da' }}
          >
            {svgComponentTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
            When selected, uploading an image will analyze it with AI to update the configurator preview SVG.
          </small>
          {svgStatus && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: svgStatus.success ? '#d4edda' : '#f8d7da',
              color: svgStatus.success ? '#155724' : '#721c24',
              fontSize: '0.9em'
            }}>
              {svgStatus.success ? '✓' : '✗'} {svgStatus.message}
            </div>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          disabled={uploading}
        />
        {uploading && <span className="upload-status">Uploading{svgComponentType ? ' & generating SVG' : ''}...</span>}
        <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
          Max size: 5MB. Formats: JPG, PNG, GIF, WebP, SVG
        </small>
      </div>

      <div className="editor-actions">
        <button type="submit" className="btn btn-small btn-primary">Save</button>
        <button type="button" className="btn btn-small" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

export default AdminDashboard;
